/**
 * Referral partner statement: what a partner sent us over a period, what came
 * of each lead, the money each completed job made, the partner's share, and
 * what we owe them. Built for the partner to read (sent like the technician
 * report), so it never shows technician names, internal notes or lost reasons.
 *
 * Numbers come from the DB fee engine (referral_fee_sync_lead): a fee row
 * exists once a job is completed (or per lead), and its base_amount is the
 * figure the share was taken from.
 */
import type { FeeBasis, FeeStatus, PayWhen } from '@/hooks/useReferralPartners';

export interface StatementJob {
  id: string;
  job_number: string | null;
  status: string;
  scheduled_date: string | null;
  job_date: string | null;
  invoice_total: number | null;
  revenue_before_gst: number | null;
  payment_status: string | null;
}

export interface StatementFee {
  amount: number;
  status: FeeStatus;
  base_amount: number | null;
  rate: number;
  paid_at: string | null;
  void_reason: string | null;
}

export interface StatementLeadInput {
  id: string;
  lead_number: string;
  created_at: string;
  customer_name: string | null;
  customer_suburb: string | null;
  status: string;
  job: StatementJob | null;
  fee: StatementFee | null;
}

export interface StatementPartner {
  name: string;
  fee_basis: FeeBasis;
  fee_amount: number;
  fee_percent: number;
  pay_when: PayWhen;
}

export type StatementOutcome =
  | 'completed'
  | 'booked'          // job scheduled
  | 'in_progress'
  | 'on_hold'
  | 'quoted'          // job left at quote_sent, or the lead's estimate is out
  | 'quote_accepted'  // estimate accepted, job not booked yet
  | 'open'            // lead still being arranged
  | 'cancelled'
  | 'not_booked';

export interface StatementRow {
  leadNumber: string;
  jobNumber: string | null;
  sentOn: string;
  customer: string;
  outcome: StatementOutcome;
  /** Scheduled date for booked jobs, completion date for completed ones. */
  outcomeDate: string | null;
  /** Booked for a day that has passed, but the job was never closed. */
  notClosed: boolean;
  saleIncTax: number | null;
  saleExTax: number | null;
  /** The figure the share is taken from (profit or revenue); null for flat fees. */
  base: number | null;
  /** Sale ex tax minus the base: parts, tolls and card fees (and tech pay for company-profit deals). */
  costs: number | null;
  fee: number | null;
  feeStatus: 'paid' | 'owed' | 'waiting' | 'not_charged' | 'pending' | 'none';
  paidOn: string | null;
}

export interface PartnerStatement {
  partner: StatementPartner;
  rows: StatementRow[];
  totals: {
    leads: number;
    completed: number;
    /** Scheduled, in progress or on hold */
    booked: number;
    /** Quote out or accepted, or the lead is still being arranged */
    quoted: number;
    notBooked: number;
    sales: number;
    salesExTax: number;
    base: number;
    share: number;
    paid: number;
    owed: number;
    waiting: number;
  };
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const PERCENT_BASES: FeeBasis[] = ['percent_revenue', 'percent_gross_profit', 'percent_profit'];
export const isPercentBasis = (b: FeeBasis) => PERCENT_BASES.includes(b);
export const showsCosts = (b: FeeBasis) => b === 'percent_gross_profit' || b === 'percent_profit';

function outcomeOf(l: StatementLeadInput): { outcome: StatementOutcome; date: string | null } {
  const j = l.job;
  if (j) {
    switch (j.status) {
      case 'completed': return { outcome: 'completed', date: j.job_date ?? j.scheduled_date };
      case 'cancelled': return { outcome: 'cancelled', date: null };
      case 'quote_sent': return { outcome: 'quoted', date: null };
      case 'in_progress': return { outcome: 'in_progress', date: null };
      case 'on_hold': return { outcome: 'on_hold', date: null };
      default: return { outcome: 'booked', date: j.scheduled_date };
    }
  }
  if (l.status === 'lost' || l.status === 'spam') return { outcome: 'not_booked', date: null };
  if (l.status === 'estimate_sent' || l.status === 'quote_sent') return { outcome: 'quoted', date: null };
  if (l.status === 'estimate_accepted') return { outcome: 'quote_accepted', date: null };
  return { outcome: 'open', date: null };
}

const BOOKED: StatementOutcome[] = ['booked', 'in_progress', 'on_hold'];
const QUOTED: StatementOutcome[] = ['quoted', 'quote_accepted', 'open'];

/** `nowIso` decides whether a booked day has passed (scheduled_date is midnight of that day, company time). */
export function buildPartnerStatement(partner: StatementPartner, leads: StatementLeadInput[], nowIso: string = new Date().toISOString()): PartnerStatement {
  const now = Date.parse(nowIso);
  const rows: StatementRow[] = leads.map((l) => {
    const { outcome, date } = outcomeOf(l);
    const notClosed = outcome === 'booked' && !!date && Date.parse(date) + 86_400_000 <= now;
    const done = outcome === 'completed';
    const f = l.fee && l.fee.status !== 'void' ? l.fee : null;
    const saleIncTax = done ? round2(num(l.job?.invoice_total)) : null;
    const saleExTax = done ? round2(num(l.job?.revenue_before_gst ?? l.job?.invoice_total)) : null;
    const base = f && f.base_amount != null ? round2(num(f.base_amount)) : null;
    const feeStatus: StatementRow['feeStatus'] =
      l.fee?.status === 'paid' ? 'paid'
        : l.fee?.status === 'payable' ? 'owed'
          : l.fee?.status === 'accrued' ? 'waiting'
            : l.fee?.status === 'void' ? 'not_charged'
              : BOOKED.includes(outcome) || QUOTED.includes(outcome) ? 'pending'
                : 'none';
    return {
      leadNumber: l.lead_number,
      jobNumber: l.job?.job_number ?? null,
      sentOn: l.created_at,
      customer: [l.customer_name?.trim(), l.customer_suburb?.trim()].filter(Boolean).join(', ') || 'Customer',
      outcome,
      outcomeDate: date,
      notClosed,
      saleIncTax,
      saleExTax,
      base,
      costs: base != null && saleExTax != null && showsCosts(partner.fee_basis) ? round2(saleExTax - base) : null,
      fee: f ? round2(num(f.amount)) : null,
      feeStatus,
      paidOn: l.fee?.status === 'paid' ? l.fee.paid_at : null,
    };
  });

  const sum = (pick: (r: StatementRow) => number | null) => round2(rows.reduce((s, r) => s + (pick(r) ?? 0), 0));
  const feeWhere = (st: StatementRow['feeStatus']) => sum((r) => (r.feeStatus === st ? r.fee : 0));
  return {
    partner,
    rows,
    totals: {
      leads: rows.length,
      completed: rows.filter((r) => r.outcome === 'completed').length,
      booked: rows.filter((r) => BOOKED.includes(r.outcome)).length,
      quoted: rows.filter((r) => QUOTED.includes(r.outcome)).length,
      notBooked: rows.filter((r) => r.outcome === 'not_booked' || r.outcome === 'cancelled').length,
      sales: sum((r) => r.saleIncTax),
      salesExTax: sum((r) => r.saleExTax),
      base: sum((r) => r.base),
      share: sum((r) => r.fee),
      paid: feeWhere('paid'),
      owed: feeWhere('owed'),
      waiting: feeWhere('waiting'),
    },
  };
}

// ---------------------------------------------------------------------------
// Wording for the partner
// ---------------------------------------------------------------------------

export const OUTCOME_LABEL: Record<StatementOutcome, string> = {
  completed: 'Completed',
  booked: 'Booked',
  in_progress: 'In progress',
  on_hold: 'On hold',
  quoted: 'Quote sent, waiting on customer',
  quote_accepted: 'Quote accepted, to be booked',
  open: 'Not booked yet',
  cancelled: 'Cancelled',
  not_booked: 'Not booked',
};

/** "Completed 22 Oct 2026", "Booked 21 Oct 2026, not closed yet", "Quote sent, waiting on customer". */
export function outcomeText(r: StatementRow, formatDate: (v: string) => string): string {
  const date = r.outcomeDate ? ` ${formatDate(r.outcomeDate)}` : '';
  return `${OUTCOME_LABEL[r.outcome]}${date}${r.notClosed ? ', not closed yet' : ''}`;
}

export function feeStatusLabel(r: StatementRow, formatDate: (v: string) => string): string {
  switch (r.feeStatus) {
    case 'paid': return r.paidOn ? `Paid ${formatDate(r.paidOn)}` : 'Paid';
    case 'owed': return 'Owed';
    case 'waiting': return 'Due once the customer pays';
    case 'not_charged': return 'No fee';
    case 'pending':
      return r.outcome === 'open' ? "If it's booked"
        : r.outcome === 'quoted' || r.outcome === 'quote_accepted' ? 'If the quote goes ahead'
          : 'Once the job is closed';
    default: return '—';
  }
}

/** The deal in the partner's own terms, e.g. "50% of each job's profit: the sale excluding GST, less parts, tolls and card fees". */
export function describeDealForPartner(p: StatementPartner, money: (n: number) => string, taxLabel: string): string {
  const exTax = taxLabel ? `excluding ${taxLabel}` : 'before tax';
  const when = p.pay_when === 'job_paid' ? 'once the customer has paid' : 'once the job is completed';
  switch (p.fee_basis) {
    case 'per_lead': return `${money(p.fee_amount)} for every lead you send.`;
    case 'per_job': return `${money(p.fee_amount)} for every completed job, payable ${when}.`;
    case 'percent_revenue': return `${p.fee_percent}% of each completed job's sale ${exTax}, payable ${when}.`;
    case 'percent_gross_profit':
      return `${p.fee_percent}% of each completed job's profit: the sale ${exTax}, less parts, tolls and card fees. Payable ${when}.`;
    case 'percent_profit':
      return `${p.fee_percent}% of each completed job's profit after all job costs, including labour. Payable ${when}.`;
  }
}

export function shareLabel(p: StatementPartner, money: (n: number) => string): string {
  if (p.fee_basis === 'per_lead') return `${money(p.fee_amount)} / lead`;
  if (p.fee_basis === 'per_job') return `${money(p.fee_amount)} / job`;
  return `${p.fee_percent}%`;
}

const baseLabel = (b: FeeBasis) => (b === 'percent_revenue' ? 'Sale ex tax' : 'Profit');

// ---------------------------------------------------------------------------
// Downloadable HTML (open it, or print it to PDF, then send it)
// ---------------------------------------------------------------------------

export interface StatementHtmlContext {
  companyName: string;
  logoUrl?: string | null;
  primaryColor?: string | null;
  periodLabel: string;
  generatedLabel: string;
  taxLabel: string;
  /** Percent, e.g. 10 for GST. With taxLabel set, the share is shown ex and inc tax. */
  taxRate?: number;
  money: (n: number) => string;
  formatDate: (v: string) => string;
  account: { owedNow: number; paidToDate: number; lastPayout: string | null };
}

/** Fees are worked out ex tax; a tax-registered partner invoices the share plus tax. */
export const shareIncTax = (share: number, taxRate: number) => Math.round(share * (1 + taxRate / 100) * 100) / 100;

const esc = (v: unknown) =>
  String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

export function generatePartnerStatementHtml(s: PartnerStatement, ctx: StatementHtmlContext): string {
  const { money, formatDate } = ctx;
  const p = s.partner;
  const t = s.totals;
  const brand = /^#[0-9a-f]{6}$/i.test(ctx.primaryColor ?? '') ? (ctx.primaryColor as string) : '#2f58c8';
  const incTax = ctx.taxLabel ? `inc ${ctx.taxLabel}` : 'total';
  const exTax = ctx.taxLabel ? `ex ${ctx.taxLabel}` : 'net';
  const pct = isPercentBasis(p.fee_basis);
  const costs = showsCosts(p.fee_basis);

  const card = (label: string, value: string, strong = false) =>
    `<div class="card${strong ? ' strong' : ''}"><div class="label">${esc(label)}</div><div class="value">${esc(value)}</div></div>`;

  const head = [
    'Sent', 'Ref', 'Customer', 'Outcome', `Sale (${incTax})`,
    ...(pct ? [`Sale (${exTax})`] : []),
    ...(costs ? ['Costs', 'Profit'] : []),
    'Your share', 'Your fee', 'Status',
  ];

  const rowsHtml = s.rows.map((r) => {
    const outcome = outcomeText(r, formatDate);
    const cells = [
      formatDate(r.sentOn),
      r.jobNumber ?? r.leadNumber,
      r.customer,
      outcome,
      r.saleIncTax != null ? money(r.saleIncTax) : '—',
      ...(pct ? [r.saleExTax != null ? money(r.saleExTax) : '—'] : []),
      ...(costs ? [r.costs != null ? money(r.costs) : '—', r.base != null ? money(r.base) : '—'] : []),
      r.fee != null ? shareLabel(p, money) : '—',
      r.fee != null ? money(r.fee) : '—',
      feeStatusLabel(r, formatDate),
    ];
    const cls = r.outcome === 'completed' ? '' : ' class="muted"';
    return `<tr${cls}>${cells.map((c, i) => `<td${i >= 4 && i < cells.length - 1 ? ' class="num"' : ''}>${esc(c)}</td>`).join('')}</tr>`;
  }).join('');

  const totalCells = [
    'Total', '', `${t.leads} lead${t.leads === 1 ? '' : 's'}`, `${t.completed} completed`,
    money(t.sales),
    ...(pct ? [money(t.salesExTax)] : []),
    ...(costs ? [money(round2(t.salesExTax - t.base)), money(t.base)] : []),
    '', money(t.share), '',
  ];

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${esc(p.name)} — referral statement — ${esc(ctx.periodLabel)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; color: #1f2937; background: #fff; padding: 28px; font-size: 14px; line-height: 1.5; }
  header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; border-bottom: 3px solid ${brand}; padding-bottom: 16px; margin-bottom: 22px; }
  header img { max-height: 56px; max-width: 200px; }
  .company { font-size: 20px; font-weight: 700; color: ${brand}; }
  h1 { font-size: 24px; color: #111827; }
  .sub { color: #6b7280; font-size: 13px; }
  .right { text-align: right; }
  .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin-bottom: 18px; }
  .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px 14px; }
  .card .label { font-size: 12px; color: #6b7280; }
  .card .value { font-size: 20px; font-weight: 700; color: #111827; }
  .card.strong { background: ${brand}; border-color: ${brand}; }
  .card.strong .label, .card.strong .value { color: #fff; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { background: ${brand}; color: #fff; text-align: left; padding: 9px 8px; font-weight: 600; white-space: nowrap; }
  td { padding: 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
  td.num, th.num { text-align: right; white-space: nowrap; }
  tr.muted td { color: #6b7280; }
  tr.total td { font-weight: 700; border-top: 2px solid #111827; border-bottom: none; color: #111827; }
  .account { margin-top: 22px; display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; }
  footer { margin-top: 24px; color: #9ca3af; font-size: 12px; }
  @media print { body { padding: 0; } th { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .card.strong { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>
<header>
  <div>${ctx.logoUrl ? `<img src="${esc(ctx.logoUrl)}" alt="${esc(ctx.companyName)}">` : `<div class="company">${esc(ctx.companyName)}</div>`}
    <h1>Referral statement: ${esc(p.name)}</h1>
    <div class="sub">${esc(ctx.periodLabel)}</div></div>
  <div class="right sub">${ctx.logoUrl ? `<div style="font-weight:600;color:#111827">${esc(ctx.companyName)}</div>` : ''}Prepared ${esc(ctx.generatedLabel)}</div>
</header>

<div class="cards">
  ${card('Leads sent', String(t.leads))}
  ${card('Jobs completed', String(t.completed))}
  ${card('Booked / in progress', String(t.booked))}
  ${card('Quoted / being arranged', String(t.quoted))}
  ${card('Not booked / cancelled', String(t.notBooked))}
  ${card(`Sales (${incTax})`, money(t.sales))}
  ${pct && p.fee_basis !== 'percent_revenue' ? card('Profit', money(t.base)) : ''}
  ${ctx.taxLabel && ctx.taxRate
    ? `${card(`Your share (${exTax})`, money(t.share), true)}
  ${card(`Your share (${incTax})`, money(shareIncTax(t.share, ctx.taxRate)), true)}`
    : card('Your share', money(t.share), true)}
</div>

<table>
  <thead><tr>${head.map((h, i) => `<th${i >= 4 && i < head.length - 1 ? ' class="num"' : ''}>${esc(h)}</th>`).join('')}</tr></thead>
  <tbody>${rowsHtml || `<tr><td colspan="${head.length}">No leads in this period.</td></tr>`}
  <tr class="total">${totalCells.map((c, i) => `<td${i >= 4 && i < totalCells.length - 1 ? ' class="num"' : ''}>${esc(c)}</td>`).join('')}</tr></tbody>
</table>

<div class="account">
  ${card('This period: paid', money(t.paid))}
  ${card('This period: owed', money(t.owed))}
  ${t.waiting > 0 ? card('This period: due once customers pay', money(t.waiting)) : ''}
  ${card('Total owed to you now', money(ctx.account.owedNow), true)}
  ${card('Paid to you to date', money(ctx.account.paidToDate))}
  ${ctx.account.lastPayout ? card('Last payment', ctx.account.lastPayout) : ''}
</div>

<footer>${esc(ctx.companyName)} · Amounts in this statement come from our job records on the date it was prepared.${pct ? ` "${esc(baseLabel(p.fee_basis))}" is the figure your share is taken from.` : ''}</footer>
</body></html>`;
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

const csvCell = (v: string | number | null | undefined) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function partnerStatementCsv(s: PartnerStatement, formatDate: (v: string) => string): string {
  const pct = isPercentBasis(s.partner.fee_basis);
  const costs = showsCosts(s.partner.fee_basis);
  const header = ['Sent', 'Lead', 'Job', 'Customer', 'Outcome', 'Outcome date', 'Sale inc tax',
    ...(pct ? ['Sale ex tax'] : []), ...(costs ? ['Costs', 'Profit'] : []), 'Fee', 'Fee status'];
  const rows = s.rows.map((r) => [
    formatDate(r.sentOn), r.leadNumber, r.jobNumber ?? '', r.customer, outcomeText(r, formatDate), r.outcomeDate ? formatDate(r.outcomeDate) : '',
    r.saleIncTax ?? '', ...(pct ? [r.saleExTax ?? ''] : []), ...(costs ? [r.costs ?? '', r.base ?? ''] : []),
    r.fee ?? '', feeStatusLabel(r, formatDate),
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
}
